import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import { Button, Spinner } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import { faEdit } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useParams } from 'react-router-dom';

import { ANNUALIZED_GMV } from 'lib/networking/endpoints';
import { backgroundGrey7, white } from 'lib/css/colors';
import BrandDetailsSection from 'components/upb_viewer/pdp_components/BrandDetailsSection';
import { PRODUCT_DETAIL_VIEW_TABS } from 'lib/constants';
import { ProductType } from 'lib/enums/ProductType';
import { squidInk } from 'lib/css/colors';
import useGet from 'lib/hooks/useGet';
import UserPermission from 'lib/enums/UserPermission';
import useUserPermissions from 'lib/hooks/useUserPermissions';

function ProductHeaderSection({
  product,
  productType,
  showGMVOnPage = false,
  isCustomData = false,
}) {
  const { viewGMV: viewGMVParam } = useParams();
  const viewGMV = viewGMVParam === 'true';

  const hasUserPermissions = useUserPermissions();
  // eslint-disable-next-line max-len
  const productEditLink = `/global-product-detail/${product.global_product_id || product.id}/${
    product.id
  }/${productType}`;
  const editLinkWithTab = productEditLink + `?tab=${PRODUCT_DETAIL_VIEW_TABS.ATTRIBUTES_V1}`;
  const canEditProduct = useMemo(
    () => hasUserPermissions([UserPermission.EDIT_PRODUCT]),
    [hasUserPermissions],
  );

  const SKU = product.manufacturer_sku || product.vendor_sku;

  // for distributors, we use the product id and the category level is product
  // for mfr and global products, we use the global product id and the category level is global
  const { data, loading: GMVLoading } = useGet(
    ANNUALIZED_GMV,
    {
      _id: productType === ProductType.MANUFACTURER ? product.global_product_id : product.id,
      category_level: productType === ProductType.DISTRIBUTOR ? 'product' : 'global',
      product_type: productType,
    },
    !product.id || !viewGMV,
  );

  // Conditionally include SKU in subtitleText only if productType is not "global"
  const subtitleTextParts = [product.pack_size];
  if (productType !== ProductType.GLOBAL && SKU) {
    subtitleTextParts.push(`SKU# ${SKU}`);
  }

  const subtitleText = subtitleTextParts.filter(item => item).join(' | ');

  return (
    <>
      <div className={css(styles.productName)}>{product.name}</div>
      <BrandDetailsSection brand={product.brand} manufacturer={product.manufacturer} />
      <div className="text-muted  mb-2">{subtitleText}</div>
      {canEditProduct && !showGMVOnPage && !isCustomData && (
        <div>
          <Button
            variant="outline-primary"
            target="_blank"
            className={css(styles.editButton)}
            href={editLinkWithTab}
          >
            <FontAwesomeIcon icon={faEdit} className={css(styles.editIcon)} />
            Edit
          </Button>
        </div>
      )}
      {viewGMV && (
        <span className={css(styles.contractGMVDark)}>
          Annualized Contract GMV :
          <span className={css(styles.gmvValue)}>
            {GMVLoading ? (
              <Spinner animation="border" role="status" size="sm" className="ml-4" />
            ) : data?.total_gmv ? (
              ` $${Number(data.total_gmv?.toFixed(2)).toLocaleString('en-US')}`
            ) : (
              'N/A'
            )}
          </span>
        </span>
      )}
    </>
  );
}

ProductHeaderSection.propTypes = {
  product: PropTypes.object,
  productType: PropTypes.string,
  showGMVOnPage: PropTypes.bool,
  isCustomData: PropTypes.bool,
};

const styles = StyleSheet.create({
  productName: {
    fontSize: 28,
    color: squidInk,
    fontWeight: 800,
  },
  editButton: {
    borderColor: squidInk,
    color: squidInk,
    fontSize: 14,
    fontWeight: 600,
    marginBottom: 24,
    paddingLeft: 30,
    paddingRight: 30,
  },
  editIcon: {
    marginRight: 8,
  },
  contractGMVDark: {
    background: backgroundGrey7,
    color: white,
    borderRadius: 4,
    textAlign: 'center',
    padding: '5px 10px',
    fontSize: 17,
  },
  gmvValue: {
    fontWeight: 'bold',
  },
});

export default ProductHeaderSection;
